/**
 * UCC Search Hooks
 *
 * React Query hooks for UCC debtor-name search and lien summary.
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import type { components } from '@aaimplatform/institutional-types/ucc-search';
import { uccSearchClient } from '../api/typed-clients';
import { retryUnlessClientError, unwrapUccResponse } from './ucc-response';

/** Request body of POST /ucc-searches (schema-registry/ucc-search.openapi.yaml). */
export type UccSearchRequest = components['schemas']['UccSearchRequest'];

/**
 * The debtor name the availability read sends. GET /ucc-searches/by-debtor
 * requires one (minLength 1); the read's rows are discarded and only its HTTP
 * status is used.
 */
export const AVAILABILITY_CHECK_DEBTOR_NAME = 'availability-check';

/**
 * Whether the UCC search service answers, read once when the Search page
 * loads. GET /ucc-searches/by-debtor declares 200, 400 and 500 and no 404
 * (schema-registry/ucc-search.openapi.yaml), so a 404 there means the path is
 * not served: the service has no deployment (GH #1734). The page says so
 * before the operator fills in the form, rather than after the first POST.
 */
export function useUccSearchAvailability(tenantId: string) {
  return useQuery({
    queryKey: ['uccSearchAvailability', tenantId],
    queryFn: async () => {
      const result = await uccSearchClient.GET('/ucc-searches/by-debtor', {
        params: { query: { tenantId, debtorName: AVAILABILITY_CHECK_DEBTOR_NAME, limit: 1 } },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!tenantId,
    retry: retryUnlessClientError,
  });
}

export function useInitiateSearch() {
  return useMutation({
    mutationFn: async (body: UccSearchRequest) => {
      const result = await uccSearchClient.POST('/ucc-searches', { body });
      return unwrapUccResponse(result);
    },
  });
}

export function useSearchResults(searchId: string | undefined) {
  return useQuery({
    queryKey: ['uccSearchResults', searchId],
    queryFn: async () => {
      const result = await uccSearchClient.GET('/ucc-searches/{searchId}/results', {
        params: { path: { searchId: searchId! } },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!searchId,
  });
}

export function useLienSummary(searchId: string | undefined) {
  return useQuery({
    queryKey: ['uccLienSummary', searchId],
    queryFn: async () => {
      const result = await uccSearchClient.GET('/ucc-searches/{searchId}/lien-summary', {
        params: { path: { searchId: searchId! } },
      });
      return unwrapUccResponse(result);
    },
    enabled: !!searchId,
  });
}
